import * as joint from '@clientio/rappid';
import { TranslocoService } from '@jsverse/transloco';
import { ComponentConverter } from 'shared/domain';

import {
  ActivityDefinitionDto,
  ComponentDto,
  FlowchartActionDto,
  FlowchartContainerDto,
  FlowchartDecisionNodeDto,
  FlowchartElementType,
  FlowchartLinkDto,
  FlowchartNodeDto,
  FlowchartSelectionBehaviorDto,
  FlowchartStartNodeDto,
  FlowchartSubflowDto,
  FlowchartTextBlockDto,
} from '@treetop/shared/model';

import { FlowchartActionNode } from './FlowchartActionNode';
import { FlowchartContainerNode } from './FlowchartContainerNode';
import { FlowchartDecisionNode } from './FlowchartDecisionNode';
import { FlowchartSelectionBehavior } from './FlowchartSelectionBehavior';
import { FlowchartStartNode } from './FlowchartStartNode';
import { FlowchartSubflowNode } from './FlowchartSubflowNode';
import { FlowchartTextBlockNode } from './FlowchartTextBlockNode';
import { IFlowchartNode } from './IFlowchartNode';
import { FlowchartSidebarStoreService } from '../../../../../guideline/feature-sidebar/src/lib/infrastructure/flowchart-sidebar-store.service';
import OperationsService from '../infrastructure/operations/operations.service';
import SubflowService from '../infrastructure/operations/subflow.service';

export class FlowchartNode<T extends FlowchartNodeDto> implements IFlowchartNode {
  node:
    | FlowchartStartNode
    | FlowchartActionNode
    | FlowchartDecisionNode
    | FlowchartTextBlockNode
    | FlowchartContainerNode
    | FlowchartSubflowNode;

  constructor(public element: T) {
    this.element = element;
    this.node = this.createNode();
  }

  addOutgoingLink(link: FlowchartLinkDto) {
    this.node.addOutgoingLink(link);
  }

  addToGraph(
    graph: joint.dia.Graph,
    paper: joint.dia.Paper,
    operationsService: OperationsService,
    subflowService: SubflowService,
    translocoService?: TranslocoService,
    allowEvents = false,
  ): void {
    this.node.addToGraph(graph, paper, operationsService, subflowService, translocoService, allowEvents);
  }

  removeOutgoingLink(linkIndex: number) {
    this.node.removeOutgoingLink(linkIndex);
  }

  setPosition(elementView: joint.dia.ElementView): void {
    this.node.setPosition(elementView);
  }

  setSize(elementView: joint.dia.ElementView): void {
    this.node.setSize(elementView);
  }

  getElement() {
    return this.node.getElement();
  }

  setPropertiesForInit(element: joint.dia.Cell): void {
    this.node.setPropertiesForInit(element);
  }

  async setPropertiesAfterSearch(
    definition: ComponentDto,
    medicationRequests: ActivityDefinitionDto[],
    componentConverter: ComponentConverter,
    selectedElementView?: joint.dia.ElementView,
    translocoService?: TranslocoService,
    flowchartSidebarStoreService?: FlowchartSidebarStoreService,
  ): Promise<void> {
    await this.node.setPropertiesAfterSearch(
      definition,
      medicationRequests,
      componentConverter,
      selectedElementView,
      translocoService,
      flowchartSidebarStoreService,
    );
  }

  private createNode() {
    if (this.element.elementType !== undefined && this.element.elementType !== null) {
      switch (this.element.elementType) {
        case FlowchartElementType.FlowchartStartNode:
          return new FlowchartStartNode(this.element as FlowchartStartNodeDto);
        case FlowchartElementType.FlowchartAction:
          return new FlowchartActionNode(this.element as FlowchartActionDto);
        case FlowchartElementType.FlowchartDecisionNode:
          return new FlowchartDecisionNode(this.element as FlowchartDecisionNodeDto);
        case FlowchartElementType.FlowchartSelectionBehavior:
          return new FlowchartSelectionBehavior(this.element as FlowchartSelectionBehaviorDto);
        case FlowchartElementType.FlowchartTextBlock:
          return new FlowchartTextBlockNode(this.element as FlowchartTextBlockDto);
        case FlowchartElementType.FlowchartContainer:
          return new FlowchartContainerNode(this.element as FlowchartContainerDto);
        case FlowchartElementType.FlowchartSubflow:
          return new FlowchartSubflowNode(this.element as FlowchartSubflowDto);
        default:
          return null;
      }
    } else {
      if (this.element instanceof FlowchartStartNodeDto) {
        return new FlowchartStartNode(this.element as FlowchartStartNodeDto);
      } else if (this.element instanceof FlowchartDecisionNodeDto) {
        return new FlowchartDecisionNode(this.element as FlowchartDecisionNodeDto);
      } else if (this.element instanceof FlowchartSelectionBehaviorDto) {
        return new FlowchartSelectionBehavior(this.element as FlowchartSelectionBehaviorDto);
      } else if (this.element instanceof FlowchartTextBlockDto) {
        return new FlowchartTextBlockNode(this.element as FlowchartTextBlockDto);
      } else if (this.element instanceof FlowchartActionDto) {
        return new FlowchartActionNode(this.element as FlowchartActionDto);
      } else if (this.element instanceof FlowchartContainerDto) {
        return new FlowchartContainerNode(this.element as FlowchartContainerDto);
      } else if (this.element instanceof FlowchartSubflowDto) {
        return new FlowchartSubflowNode(this.element as FlowchartSubflowDto);
      }
    }
  }
}
